import {
  mergeClasses,
  Skeleton,
  SkeletonItem,
} from "@fluentui/react-components";
import { SkeletonTable } from "components/skeletonTable/SkeletonTable";
import { ChevronLeftRegular } from "@fluentui/react-icons";
import {
  getDiagnosticById,
  getDistributionRespodents,
} from "apps/diagnostics/api";
import { getSurveyTemplate } from "apps/project-iq/api.js";
import { ManageObjectives } from "apps/diagnostics/components/manageObjectives/ManageObjectives";
import { ManageDistributions } from "apps/diagnostics/components/manageDistributions/ManageDistributions";
import { IDiagnostic as IFullDiagnostic } from "apps/diagnostics/models/IDiagnostic";
import { DiagnosticChat } from "apps/project-iq/components/DiagnosticChat/DiagnosticChat";
import { DiagnosticRun } from "apps/project-iq/components/DiagnosticRun/DiagnosticRun";
import { DiagnosticResultFiles } from "apps/project-iq/components/DiagnosticResultFiles/DiagnosticResultFiles";
import { DIAGNOSTIC_METHOD_ICONS } from "apps/project-iq/components/icons/ProjectIqIcons";
import { useWorkspaceDiagnostic } from "apps/project-iq/hooks/useWorkspaceDiagnostic";
import { IDiagnostic } from "apps/project-iq/models/IDiagnostic";
import { IDiagnosticTemplate } from "apps/project-iq/models/IDiagnosticTemplate";
import { CreateDiagnosticDialog } from "apps/project-iq/components/CreateDiagnosticDialog/CreateDiagnosticDialog";
import { DiagnosticOverviewTab } from "./DiagnosticOverviewTab";
import {
  deriveDiagnosticStatus,
  METHOD_LABELS,
  STATUS_LABELS,
} from "apps/project-iq/utils/diagnosticUtils";
import { IUserDetails } from "models/IUserDetails";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { updateDiagnosticInSlice } from "redux/diagnosticsSlice";
import { formatDate } from "utils/generalUtils";
import { useDiagnosticDetailPageStyles } from "./DiagnosticDetailPage.styles";

const DIAGNOSTIC_TAB = {
  OVERVIEW: "Overview",
  RUN: "Run",
  RESULTS: "Results",
} as const;
type DiagnosticTab = (typeof DIAGNOSTIC_TAB)[keyof typeof DIAGNOSTIC_TAB];
const DIAGNOSTIC_TABS = Object.values(DIAGNOSTIC_TAB) as DiagnosticTab[];

const RUN_SUB_TAB = {
  OBJECTIVES: "Objectives",
  DISTRIBUTIONS: "Distributions",
} as const;
type RunSubTab = (typeof RUN_SUB_TAB)[keyof typeof RUN_SUB_TAB];
const RUN_SUB_TABS = Object.values(RUN_SUB_TAB) as RunSubTab[];

export const DiagnosticDetailPage = (): JSX.Element => {
  const { workspaceId, diagnosticId } = useParams<{
    workspaceId: string;
    diagnosticId: string;
  }>();

  const navigate = useNavigate();
  const location = useLocation();
  const styles = useDiagnosticDetailPageStyles();
  const dispatch = useDispatch();

  const locationState = location.state as {
    initialTab?: string;
  } | null;
  const [activeTab, setActiveTab] = useState<DiagnosticTab>(
    (locationState?.initialTab as DiagnosticTab) ?? DIAGNOSTIC_TAB.OVERVIEW,
  );

  const [fullDiagnostic, setFullDiagnostic] = useState<IFullDiagnostic | null>(
    null,
  );
  const [respondents, setRespondents] = useState<
    Record<string, { users: IUserDetails[] }>
  >({});
  const [isFullDiagnosticLoading, setIsFullDiagnosticLoading] = useState(false);
  const [runSubTab, setRunSubTab] = useState<RunSubTab>(RUN_SUB_TAB.OBJECTIVES);
  const [template, setTemplate] = useState<IDiagnosticTemplate | null>(null);
  const [isTemplateError, setIsTemplateError] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isOverviewReloading, setIsOverviewReloading] = useState(false);
  const savedDiagnosticRef = useRef<IDiagnostic | undefined>(undefined);

  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const fetchFullDiagnostic = useCallback(async () => {
    if (!diagnosticId) return;

    setIsFullDiagnosticLoading(true);
    try {
      const result = await getDiagnosticById({ id: diagnosticId });
      setFullDiagnostic(result);
      dispatch(updateDiagnosticInSlice(result));
    } finally {
      setIsFullDiagnosticLoading(false);
    }
  }, [diagnosticId, dispatch]);

  const refreshRespondents = useCallback(async () => {
    if (!diagnosticId) return;
    try {
      const result = await getDistributionRespodents({ id: diagnosticId });
      setRespondents(result);
    } catch {
      // respondents are non-critical — leave existing state on failure
    }
  }, [diagnosticId]);

  useEffect(() => {
    if (activeTab === DIAGNOSTIC_TAB.RUN) {
      fetchFullDiagnostic();
      refreshRespondents();
    }
  }, [activeTab, fetchFullDiagnostic, refreshRespondents]);

  const {
    data: diagnostic,
    isLoading,
    isError,
  } = useWorkspaceDiagnostic(diagnosticId ?? "");

  useEffect(() => {
    if (!isOverviewReloading) return;
    if (diagnostic === savedDiagnosticRef.current) return;
    if (
      diagnostic?.questionBankType === "template" &&
      !isTemplateError &&
      template?.id !== diagnostic?.templateId
    ) {
      return;
    }
    setIsOverviewReloading(false);
  }, [diagnostic, isOverviewReloading, template, isTemplateError]);

  useEffect(() => {
    if (!diagnostic?.templateId || diagnostic.questionBankType !== "template") {
      setTemplate(null);
      return;
    }
    setIsTemplateError(false);
    getSurveyTemplate(diagnostic.templateId)
      .then((t: IDiagnosticTemplate) => setTemplate(t))
      .catch(() => setIsTemplateError(true));
  }, [diagnostic?.templateId, diagnostic?.questionBankType]);

  if (isLoading) {
    return (
      <div
        className={styles.root}
        aria-busy="true"
        aria-label="Loading diagnostic details"
      >
        <Skeleton>
          <nav aria-hidden="true" className={styles.breadcrumbBar}>
            <SkeletonItem
              shape="rectangle"
              size={32}
              className={styles.skeletonBreadcrumb}
            />
          </nav>
          <div className={styles.contentArea}>
            <div className={mergeClasses(styles.hero, styles.skeletonRow)}>
              <SkeletonItem shape="square" size={40} />
              <div className={styles.skeletonCol}>
                <div className={styles.skeletonRow}>
                  <SkeletonItem
                    shape="rectangle"
                    size={28}
                    className={styles.skeletonHeroName}
                  />
                  <SkeletonItem
                    shape="rectangle"
                    size={20}
                    className={styles.skeletonStatusPill}
                  />
                </div>
                <SkeletonItem
                  shape="rectangle"
                  size={16}
                  className={styles.skeletonHeroSubline}
                />
              </div>
            </div>
            <div className={styles.tabBar} aria-hidden="true">
              <SkeletonItem
                shape="rectangle"
                size={16}
                className={styles.skeletonTabPrimary}
              />
              <SkeletonItem
                shape="rectangle"
                size={16}
                className={styles.skeletonTabSecondary}
              />
              <SkeletonItem
                shape="rectangle"
                size={16}
                className={styles.skeletonTabTertiary}
              />
            </div>
            <SkeletonItem
              shape="rectangle"
              className={styles.skeletonContent}
            />
          </div>
        </Skeleton>
      </div>
    );
  }

  if (isError || !diagnostic) {
    return (
      <div className={styles.root}>
        <nav aria-label="Breadcrumb" className={styles.breadcrumbBar}>
          <button
            className={styles.backButton}
            onClick={() =>
              navigate(`/workspace/${workspaceId}/nh-diagnostic-iq`)
            }
            aria-label="Back to NH Diagnostic IQ"
          >
            <ChevronLeftRegular aria-hidden="true" />
            NH Diagnostic IQ
          </button>
          <span className={styles.breadcrumbSeparator} aria-hidden="true">
            /
          </span>
          <span
            className={styles.breadcrumbDiagnosticName}
            aria-current="page"
            role="status"
          >
            Not found
          </span>
        </nav>
      </div>
    );
  }

  const status = deriveDiagnosticStatus(diagnostic);

  const statusPillClass = mergeClasses(
    styles.statusPill,
    status === "in_progress" && styles.statusPillInProgress,
    status === "completed" && styles.statusPillCompleted,
    status === "draft" && styles.statusPillDraft,
  );

  return (
    <div className={styles.root}>
      <nav aria-label="Breadcrumb" className={styles.breadcrumbBar}>
        <button
          className={styles.backButton}
          onClick={() => navigate(`/workspace/${workspaceId}/nh-diagnostic-iq`)}
          aria-label="Back to NH Diagnostic IQ"
        >
          <ChevronLeftRegular aria-hidden="true" />
          NH Diagnostic IQ
        </button>
        <span className={styles.breadcrumbSeparator} aria-hidden="true">
          /
        </span>
        <span className={styles.breadcrumbDiagnosticName} aria-current="page">
          {diagnostic.name}
        </span>
      </nav>

      <div className={styles.contentArea}>
        <section className={styles.hero}>
          <span className={styles.methodIconTile} aria-hidden="true">
            {DIAGNOSTIC_METHOD_ICONS[diagnostic.method]}
          </span>
          <div className={styles.heroBody}>
            <div className={styles.heroNameRow}>
              <h1
                ref={headingRef}
                tabIndex={-1}
                id="diagnostic-name"
                className={styles.heroName}
              >
                {diagnostic.name}
              </h1>
              <span
                className={statusPillClass}
                aria-label={`Status: ${STATUS_LABELS[status]}`}
              >
                {STATUS_LABELS[status]}
              </span>
            </div>
            <div className={styles.heroSubLine}>
              <span className={styles.heroMethodLabel}>
                {METHOD_LABELS[diagnostic.method]}
              </span>
              <span className={styles.heroCreatedDate}>
                {" · Created "}
                {formatDate(diagnostic.createdAt)}
              </span>
            </div>
          </div>
        </section>

        <div
          className={styles.tabBar}
          aria-label="Diagnostic sections"
          role="tablist"
        >
          {DIAGNOSTIC_TABS.map((tab) => {
            const isActive = tab === activeTab;
            return (
              <button
                key={tab}
                id={`tab-${tab}`}
                role="tab"
                aria-selected={isActive}
                aria-controls={`tabpanel-${tab}`}
                onClick={() => setActiveTab(tab)}
                className={mergeClasses(
                  styles.tab,
                  isActive && styles.tabActive,
                )}
              >
                {tab}
              </button>
            );
          })}
        </div>

        <div
          id={`tabpanel-${activeTab}`}
          role="tabpanel"
          aria-labelledby={`tab-${activeTab}`}
        >
          {activeTab === DIAGNOSTIC_TAB.OVERVIEW && (
            <DiagnosticOverviewTab
              diagnostic={diagnostic}
              template={template}
              isLoading={
                isOverviewReloading ||
                (diagnostic.questionBankType === "template" &&
                  !template &&
                  !isTemplateError)
              }
              onEdit={() => setIsEditDialogOpen(true)}
            />
          )}

          {activeTab === DIAGNOSTIC_TAB.RUN &&
            diagnostic.method !== "ai_interview" &&
            diagnostic.method !== "workshops_focus_groups" &&
            diagnostic.method !== "in_person_interviews" && (
              <DiagnosticRun
                diagnosticId={diagnostic.id}
                method={diagnostic.method}
                isConfigured={
                  diagnostic.status === "in_progress" ||
                  diagnostic.status === "completed"
                }
              />
            )}

          {(diagnostic.method === "workshops_focus_groups" ||
            diagnostic.method === "in_person_interviews") && (
            <div
              style={{
                display: activeTab === DIAGNOSTIC_TAB.RUN ? undefined : "none",
              }}
            >
              <div className={styles.diagnosticChatCard}>
                <DiagnosticChat
                  key={diagnostic.id}
                  workspaceId={workspaceId ?? ""}
                  diagnosticId={diagnostic.id}
                  diagnosticName={diagnostic.name}
                  deliveryMethod={diagnostic.method}
                  chatConversationId={diagnostic.chatConversationId}
                />
              </div>
            </div>
          )}

          {activeTab === DIAGNOSTIC_TAB.RUN &&
            diagnostic.method === "ai_interview" && (
              <div className={styles.tabContentFullWidth}>
                <div className={styles.runCard}>
                  <div
                    className={styles.runSubTabBar}
                    role="tablist"
                    aria-label="Run sections"
                  >
                    {RUN_SUB_TABS.map((sub) => (
                      <button
                        key={sub}
                        role="tab"
                        aria-selected={runSubTab === sub}
                        onClick={() => setRunSubTab(sub)}
                        className={mergeClasses(
                          styles.runSubTab,
                          runSubTab === sub && styles.runSubTabActive,
                        )}
                      >
                        {sub}
                      </button>
                    ))}
                  </div>
                  {isFullDiagnosticLoading ? (
                    <SkeletonTable
                      rows={5}
                      columns={
                        runSubTab === RUN_SUB_TAB.OBJECTIVES
                          ? ["Name", "Type", "Description"]
                          : ["Name", "Respondents", "Status"]
                      }
                    />
                  ) : fullDiagnostic ? (
                    runSubTab === RUN_SUB_TAB.OBJECTIVES ? (
                      <ManageObjectives
                        diagnosticId={diagnostic.id}
                        objectives={fullDiagnostic.objectives ?? []}
                        onObjectivesChange={(updatedObjectives) =>
                          setFullDiagnostic((prev) =>
                            prev
                              ? { ...prev, objectives: updatedObjectives }
                              : prev,
                          )
                        }
                      />
                    ) : (
                      <ManageDistributions
                        diagnosticId={diagnostic.id}
                        distributions={fullDiagnostic.distributions ?? []}
                        objectives={fullDiagnostic.objectives ?? []}
                        respondents={respondents}
                        onDistributionsChange={(updatedDistributions) => {
                          const updated = {
                            ...fullDiagnostic,
                            distributions: updatedDistributions,
                          };
                          setFullDiagnostic(updated);
                          dispatch(updateDiagnosticInSlice(updated));
                        }}
                        onRefreshRespondents={refreshRespondents}
                      />
                    )
                  ) : null}
                </div>
              </div>
            )}

          {activeTab === DIAGNOSTIC_TAB.RESULTS && workspaceId && (
            <DiagnosticResultFiles
              diagnosticId={diagnostic.id}
              workspaceId={workspaceId}
            />
          )}
        </div>
      </div>

      {workspaceId && (
        <CreateDiagnosticDialog
          isOpen={isEditDialogOpen}
          workspaceId={workspaceId}
          mode="edit"
          diagnostic={diagnostic}
          onSaved={() => {
            savedDiagnosticRef.current = diagnostic;
            setIsOverviewReloading(true);
          }}
          onDismiss={() => setIsEditDialogOpen(false)}
        />
      )}
    </div>
  );
};
